import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
// import { sankey, sankeyCenter, sankeyLinkHorizontal } from "d3-sankey";
import * as d3Sankey from "d3-sankey";

import { portSummaryProp } from "@pages/homepage/asset-auto-discovery/assetAutoDiscoveryTypes";

interface Node {
  id: string;
  [key: string]: any;
}

interface Link {
  source: string;
  target: string;
  value: number;
}

interface SankeyChartOptions {
  format?: string | ((value: number) => string);
  align?: "justify" | "left" | "right" | "center";
  nodeId?: (d: Node) => string;
  nodeGroup?: (d: Node) => string | number;
  nodeGroups?: (string | number)[];
  nodeLabel?: (d: Node) => string;
  nodeTitle?: (d: Node) => string;
  nodeAlign?: string;
  nodeSort?: (
    a: d3Sankey.SankeyNodeMinimal<any, any>,
    b: d3Sankey.SankeyNodeMinimal<any, any>,
  ) => number | null | undefined;
  nodeWidth?: number;
  nodePadding?: number;
  nodeLabelPadding?: number;
  nodeStroke?: string;
  nodeStrokeWidth?: number;
  nodeStrokeOpacity?: number;
  nodeStrokeLinejoin?: string;
  linkSource?: (d: Link) => string;
  linkTarget?: (d: Link) => string;
  linkValue?: (d: Link) => number;
  // linkPath?: (d: d3Sankey.SankeyLink<Node, Link>) => string;
  linkPath?: any;
  linkTitle?: (d: d3Sankey.SankeyLink<Node, Link>) => string;
  linkColor?: "source" | "target" | "source-target" | string;
  linkStrokeOpacity?: number;
  linkMixBlendMode?: string;
  colors?: readonly string[];
  width?: number;
  height?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
}

function SankeyChart(
  {
    nodes, // an iterable of node objects (typically [{id}, …]); implied by links if missing
    links, // an iterable of link objects (typically [{source, target}, …])
    svgRef,
  }: { nodes?: Node[]; links: Link[]; svgRef: React.RefObject<SVGSVGElement> },
  {
    // format = ",", // a function or format specifier for values in titles
    align = "justify", // convenience shorthand for nodeAlign
    nodeId = (d) => d.id, // given d in nodes, returns a unique identifier (string)
    nodeGroup, // given d in nodes, returns an (ordinal) value for color
    nodeGroups, // an array of ordinal values representing the node groups
    nodeLabel, // given d in (computed) nodes, text to label the associated rect
    nodeTitle = (d) => `${d.id}\n${d.value}`, // given d in (computed) nodes, hover text
    nodeAlign = align, // Sankey node alignment strategy: left, right, justify, center
    // nodeSort = ()=>null, // comparator function to order nodes
    nodeSort = (a, b) =>
      a.value !== undefined && b.value !== undefined ? b.value - a.value : 0, // comparator function to order nodes
    nodeWidth = 15, // width of node rects
    nodePadding = 10, // vertical separation between adjacent nodes
    nodeLabelPadding = 6, // horizontal separation between node and label
    nodeStroke = "currentColor", // stroke around node rects
    nodeStrokeWidth, // width of stroke around node rects, in pixels
    nodeStrokeOpacity, // opacity of stroke around node rects
    nodeStrokeLinejoin, // line join for stroke around node rects
    linkSource = ({ source }) => source, // given d in links, returns a node identifier string
    linkTarget = ({ target }) => target, // given d in links, returns a node identifier string
    linkValue = ({ value }) => value, // given d in links, returns the quantitative value
    linkPath = d3Sankey.sankeyLinkHorizontal(), // given d in (computed) links, returns the SVG path
    linkTitle = (d) => `${d.source} → ${d.target}\n${d.value}`, // given d in (computed) links
    linkColor = "source-target", // source, target, source-target, or static color
    linkStrokeOpacity = 0.5, // link stroke opacity
    linkMixBlendMode = "normal", // link blending mode
    colors = d3.schemeTableau10, // array of colors
    width = 640, // outer width, in pixels
    height = 400, // outer height, in pixels
    marginTop = 5, // top margin, in pixels
    marginRight = 1, // right margin, in pixels
    marginBottom = 5, // bottom margin, in pixels
    marginLeft = 1, // left margin, in pixels
  }: SankeyChartOptions = {},
) {
  // Convert nodeAlign from a name to a function (since d3-sankey is not part of core d3).
  const nodeAlignFunction: (
    d: d3Sankey.SankeyNodeMinimal<any, any>,
    n: number,
  ) => number =
    typeof nodeAlign !== "function"
      ? ({
          left: d3Sankey.sankeyLeft,
          right: d3Sankey.sankeyRight,
          center: d3Sankey.sankeyCenter,
        }[nodeAlign] ?? d3Sankey.sankeyJustify)
      : nodeAlign;

  // Compute values.
  const LS = d3.map(links, linkSource).map(intern);
  const LT = d3.map(links, linkTarget).map(intern);
  const LV = d3.map(links, linkValue);
  const nodeData =
    nodes === undefined
      ? Array.from(d3.union(LS, LT), (id) => ({ id }))
      : nodes;
  const N = d3.map(nodeData, nodeId).map(intern);
  const G = nodeGroup == null ? null : d3.map(nodeData, nodeGroup).map(intern);

  // Replace the input nodes and links with mutable objects for the simulation.
  const mutableNodes: d3Sankey.SankeyNode<any, any>[] = d3.map(
    nodeData,
    (_, i) => ({ id: N[i] }),
  );
  const mutableLinks: d3Sankey.SankeyLink<any, any>[] = d3.map(
    links,
    (_, i) => ({ source: LS[i], target: LT[i], value: LV[i] }),
  );

  // Ignore a group-based linkColor option if no groups are specified.
  const linkColorValue =
    !G && ["source", "target", "source-target"].includes(linkColor)
      ? "currentColor"
      : linkColor;

  // Compute default domains.
  const nodeGroupsValue: Iterable<any> =
    (G && nodeGroups === undefined ? G : nodeGroups) || [];

  // Construct the scales.
  const color =
    nodeGroup == null ? null : d3.scaleOrdinal(nodeGroupsValue, colors);

  // Compute the Sankey layout.
  d3Sankey
    .sankey()
    .nodeId(({ index: i }) => N[i ?? 0])
    .nodeAlign(nodeAlignFunction)
    .nodeWidth(nodeWidth)
    .nodePadding(nodePadding)
    .nodeSort(nodeSort)
    .extent([
      [marginLeft, marginTop],
      [width - marginRight, height - marginBottom],
    ])({ nodes: mutableNodes, links: mutableLinks });

  // Compute titles and labels using layout nodes, so as to access aggregate values.
  //   const formatFunction = typeof format !== "function" ? d3.format(format) : format;
  const Tl =
    nodeLabel === undefined
      ? N
      : nodeLabel == null
        ? null
        : d3.map(mutableNodes, nodeLabel);
  const Tt = nodeTitle == null ? null : d3.map(mutableNodes, nodeTitle);
  const Lt = linkTitle == null ? null : d3.map(mutableLinks, linkTitle);

  // A unique identifier for clip paths (to avoid conflicts).
  const uid = `O-${Math.random().toString(16).slice(2)}`;

  const svg = d3
    .select(svgRef.current)
    .attr("width", width)
    .attr("height", height)
    .attr("viewBox", [0, 0, width, height])
    .attr("style", "max-width: 100%; height: auto; height: intrinsic;");

  const node = svg
    .append("g")
    .attr("stroke", nodeStroke)
    .attr("stroke-width", nodeStrokeWidth || 0)
    .attr("stroke-opacity", nodeStrokeOpacity || 0)
    .attr("stroke-linejoin", nodeStrokeLinejoin || "miter")
    .selectAll("rect")
    .data(mutableNodes)
    .join("rect")
    .attr("x", (d) => d.x0)
    .attr("y", (d) => d.y0)
    .attr("height", (d) => d.y1 - d.y0)
    .attr("width", (d) => d.x1 - d.x0);

  if (G)
    node.attr("fill", ({ index: i }) =>
      color === null ? "currentColor" : color(G[i || 0]),
    );
  if (Tt) node.append("title").text(({ index: i }) => Tt[i]);

  const link = svg
    .append("g")
    .attr("fill", "none")
    .attr("stroke-opacity", linkStrokeOpacity)
    .selectAll("g")
    .data(mutableLinks)
    .join("g")
    .style("mix-blend-mode", linkMixBlendMode);

  if (linkColorValue === "source-target")
    link
      .append("linearGradient")
      .attr("id", (d) => `${uid}-link-${d.index}`)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", (d) => d.source.x1)
      .attr("x2", (d) => d.target.x0)
      .call((gradient) =>
        gradient
          .append("stop")
          .attr("offset", "0%")
          .attr("stop-color", ({ source: { index: i } }) =>
            color === null ? "currentColor" : G ? color(G[i]) : "currentColor",
          ),
      )
      .call((gradient) =>
        gradient
          .append("stop")
          .attr("offset", "100%")
          .attr("stop-color", ({ target: { index: i } }) =>
            color === null ? "currentColor" : G ? color(G[i]) : "currentColor",
          ),
      );

  link
    .append("path")
    .attr("d", linkPath || "")
    .attr(
      "stroke",
      linkColorValue === "source-target"
        ? ({ index: i }) => `url(#${uid}-link-${i})`
        : linkColorValue === "source"
          ? ({ source: { index: i } }) =>
              color === null ? "currentColor" : G ? color(G[i]) : "currentColor"
          : linkColorValue === "target"
            ? ({ target: { index: i } }) =>
                color === null
                  ? "currentColor"
                  : G
                    ? color(G[i])
                    : "currentColor"
            : linkColorValue,
    )
    .attr("stroke-width", ({ width }) => Math.max(1, width))
    .call(
      Lt
        ? (path) => path.append("title").text(({ index: i }) => Lt[i])
        : () => {},
    );

  if (Tl)
    svg
      .append("g")
      .attr("font-family", "sans-serif")
      .attr("font-size", 10)
      .selectAll("text")
      .data(mutableNodes)
      .join("text")
      .attr("x", (d) =>
        d.x0 < width / 2 ? d.x1 + nodeLabelPadding : d.x0 - nodeLabelPadding,
      )
      .attr("y", (d) => (d.y1 + d.y0) / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", (d) => (d.x0 < width / 2 ? "start" : "end"))
      .text(({ index: i }) => Tl[i]);

  function intern(value: any): any {
    return value !== null && typeof value === "object"
      ? value.valueOf()
      : value;
  }

  // return Object.assign(svg.node() || {}, { scales: { color } });
}

interface SankeyGraphProps {
  parentid: string;
  portSummaryData?: portSummaryProp[];
}

const SankeyGraph: React.FC<SankeyGraphProps> = ({
  parentid,
  portSummaryData,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;
    d3.select(svgRef.current).selectAll("*").remove();

    const parentElement = document.getElementById(parentid);
    if (!parentElement) {
      // Handle the error
      console.error(`No element with ID ${parentid}`);
      return;
    }
    const rect = parentElement.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "currentColor");

    if (portSummaryData && portSummaryData.length > 0) {
      const links: {
        source: string;
        target: string;
        value: number;
      }[] = [];
      portSummaryData.forEach((entry) => {
        let sport: string;
        let dport: string;

        const sportValue = parseInt(entry.sport, 0);
        const dportValue = parseInt(entry.dport, 0);

        if (sportValue > 512) {
          sport = "s512+";
        } else {
          sport = `s${sportValue.toString()}`;
        }

        if (dportValue > 512) {
          dport = "d512++";
        } else {
          dport = `d${dportValue.toString()}`;
        }

        links.push({
          source: sport,
          target: dport,
          value: entry.count,
        });
      });

      const nodes = Array.from(
        new Set(links.flatMap((l) => [l.source, l.target])),
        (name) => ({ name, id: name }),
      );

      SankeyChart(
        {
          links: links,
          nodes,
          svgRef: svgRef,
        },
        {
          nodeGroup: (d) => d.id.split(/\W/)[0], // take first word for color
          nodeAlign: "justify", // e.g., d3.sankeyJustify; set by input above
          linkColor: "source-target", // e.g., "source" or "target"; set by input above
          format: (
            (f) => (d) =>
              `${f(d)} TWh`
          )(d3.format(",.1~f")),
          width,
          height,
        },
      );
    }

    return () => {
      d3.select(svgRef.current).selectAll("*").remove();
    };
  }, [portSummaryData]);

  return <svg ref={svgRef}></svg>;
};

export default SankeyGraph;
